import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

import marketingMultinivelService, {
  MarketingMultinivelConfig,
  MarketingMultinivelNivel,
  UpdateConfigData,
  UpdateNivelData,
  CreateNivelData
} from '../../services/marketingMultinivelService';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface NivelDialogState {
  open: boolean;
  isEdit: boolean;
  nivel?: MarketingMultinivelNivel;
}

const ParametrosMarketing: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  // Estados principais
  const [config, setConfig] = useState<MarketingMultinivelConfig | null>(null);
  const [niveis, setNiveis] = useState<MarketingMultinivelNivel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Estados do formulário de configuração
  const [formConfig, setFormConfig] = useState<UpdateConfigData>({
    ativo: true,
    tipo_desconto_cadastro: 'valor',
    valor_desconto_cadastro: 15.00,
    tipo_desconto_recorrente: 'valor',
    niveis_ativos: 2
  });

  // Estados dos diálogos
  const [nivelDialog, setNivelDialog] = useState<NivelDialogState>({
    open: false,
    isEdit: false
  });
  const [nivelForm, setNivelForm] = useState<CreateNivelData>({
    nivel: 1,
    valor_desconto: 0,
    ativo: true
  });

  // Estado do snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [configData, niveisData] = await Promise.all([
        marketingMultinivelService.getConfig(),
        marketingMultinivelService.getNiveis()
      ]);

      // Validar se configData é válido
      if (!configData || Array.isArray(configData) || typeof configData !== 'object') {
        throw new Error(`Configuração inválida recebida: ${typeof configData}. Esperado: object`);
      }

      // Validar se tem as propriedades necessárias
      if (configData.ativo === undefined) {
        throw new Error('Dados de configuração incompletos - propriedade "ativo" ausente');
      }

      setConfig(configData);
      setNiveis(niveisData);
      setFormConfig({
        ativo: configData.ativo,
        tipo_desconto_cadastro: configData.tipo_desconto_cadastro,
        valor_desconto_cadastro: configData.valor_desconto_cadastro,
        tipo_desconto_recorrente: configData.tipo_desconto_recorrente,
        niveis_ativos: configData.niveis_ativos
      });

      setErrors([]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showSnackbar(`Erro ao carregar configurações: ${error instanceof Error ? error.message : 'Erro desconhecido'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      // Validar dados
      const validationErrors = marketingMultinivelService.validateConfig(formConfig);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      // Salvar configuração
      const updatedConfig = await marketingMultinivelService.updateConfig(formConfig);
      setConfig(updatedConfig);
      setErrors([]);
      showSnackbar('Configurações salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      showSnackbar('Erro ao salvar configurações', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenNivelDialog = (nivel?: MarketingMultinivelNivel) => {
    if (nivel) {
      setNivelForm({
        nivel: nivel.nivel,
        valor_desconto: nivel.valor_desconto,
        ativo: nivel.ativo
      });
      setNivelDialog({ open: true, isEdit: true, nivel });
    } else {
      const proximoNivel = Math.max(...niveis.map(n => n.nivel), 0) + 1;
      setNivelForm({
        nivel: proximoNivel <= 5 ? proximoNivel : 1,
        valor_desconto: 0,
        ativo: true
      });
      setNivelDialog({ open: true, isEdit: false });
    }
  };

  const handleCloseNivelDialog = () => {
    setNivelDialog({ open: false, isEdit: false });
    setNivelForm({ nivel: 1, valor_desconto: 0, ativo: true });
  };

  const handleSaveNivel = async () => {
    try {
      const validationErrors = marketingMultinivelService.validateNivel(nivelForm);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      if (nivelDialog.isEdit && nivelDialog.nivel) {
        await marketingMultinivelService.updateNivel(nivelDialog.nivel.id, {
          valor_desconto: nivelForm.valor_desconto,
          ativo: nivelForm.ativo ?? true
        });
        showSnackbar('Nível atualizado com sucesso!', 'success');
      } else {
        await marketingMultinivelService.createNivel(nivelForm);
        showSnackbar('Nível criado com sucesso!', 'success');
      }

      handleCloseNivelDialog();
      await loadData();
    } catch (error) {
      console.error('Erro ao salvar nível:', error);
      showSnackbar('Erro ao salvar nível', 'error');
    }
  };

  const handleDeleteNivel = async (nivel: MarketingMultinivelNivel) => {
    if (window.confirm(`Tem certeza que deseja excluir o Nível ${nivel.nivel}?`)) {
      try {
        await marketingMultinivelService.deleteNivel(nivel.id);
        showSnackbar('Nível excluído com sucesso!', 'success');
        await loadData();
      } catch (error) {
        console.error('Erro ao excluir nível:', error);
        showSnackbar('Erro ao excluir nível', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando configurações...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" gutterBottom color="primary">Configurações de Marketing Multinível</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure o sistema de indicações e bonificações multinível.
      </Typography>

      {/* Alertas de erro */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
          <Box>
            {errors.map((error, index) => (
              <Typography key={index} variant="body2">
                • {error}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={1.5}>
        {/* Configurações Gerais */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
                Configurações Gerais
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formConfig.ativo}
                      onChange={(e) => setFormConfig(prev => ({ ...prev, ativo: e.target.checked }))}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>Módulo Ativo</Typography>
                      <Tooltip title="Ativa ou desativa todo o sistema de marketing multinível">
                        <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem' }}>
                Bonificação por Cadastro
              </Typography>
              
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Desconto</InputLabel>
                    <Select
                      value={formConfig.tipo_desconto_cadastro}
                      onChange={(e) => setFormConfig(prev => ({ 
                        ...prev, 
                        tipo_desconto_cadastro: e.target.value as 'valor' | 'porcentagem' 
                      }))}
                      label="Tipo de Desconto"
                      size="small"
                    >
                      <MenuItem value="valor">Valor Fixo (R$)</MenuItem>
                      <MenuItem value="porcentagem">Porcentagem (%)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Valor do Desconto ${formConfig.tipo_desconto_cadastro === 'valor' ? '(R$)' : '(%)'}`}
                    type="number"
                    value={formConfig.valor_desconto_cadastro}
                    onChange={(e) => setFormConfig(prev => ({ 
                      ...prev, 
                      valor_desconto_cadastro: parseFloat(e.target.value) || 0 
                    }))}
                    inputProps={{ 
                      min: 0, 
                      step: 0.01,
                      max: formConfig.tipo_desconto_cadastro === 'porcentagem' ? 100 : undefined
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem' }}>
                Configurações Gerais
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo Desconto Recorrente</InputLabel>
                    <Select
                      value={formConfig.tipo_desconto_recorrente}
                      onChange={(e) => setFormConfig(prev => ({ 
                        ...prev, 
                        tipo_desconto_recorrente: e.target.value as 'valor' | 'porcentagem' 
                      }))}
                      label="Tipo Desconto Recorrente"
                      size="small"
                    >
                      <MenuItem value="valor">Valor Fixo (R$)</MenuItem>
                      <MenuItem value="porcentagem">Porcentagem (%)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Níveis Ativos"
                    type="number"
                    value={formConfig.niveis_ativos}
                    onChange={(e) => setFormConfig(prev => ({ 
                      ...prev, 
                      niveis_ativos: parseInt(e.target.value) || 1 
                    }))}
                    inputProps={{ min: 1, max: 5 }}
                    helperText="Quantos níveis de indicação estarão ativos (1-5)"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveConfig}
                  disabled={saving}
                  size="small"
                  sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
                >
                  {saving ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Resumo das Configurações */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
                Resumo
              </Typography>
              
              <Box sx={{ mb: 1.5 }}>
                <Chip 
                  icon={formConfig.ativo ? <CheckCircleIcon /> : <CancelIcon />}
                  label={formConfig.ativo ? 'Módulo Ativo' : 'Módulo Inativo'}
                  color={formConfig.ativo ? 'success' : 'default'}
                  sx={{ mb: 1.5, height: 20, fontSize: '0.7rem' }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
                Desconto por Cadastro:
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                {formConfig.tipo_desconto_cadastro === 'valor' 
                  ? marketingMultinivelService.formatCurrency(formConfig.valor_desconto_cadastro)
                  : marketingMultinivelService.formatPercentage(formConfig.valor_desconto_cadastro)
                }
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
                Níveis Ativos:
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                {formConfig.niveis_ativos} {formConfig.niveis_ativos === 1 ? 'nível' : 'níveis'}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
                Tipo Recorrente:
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                {formConfig.tipo_desconto_recorrente === 'valor' ? 'Valor Fixo' : 'Porcentagem'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuração de Níveis */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                Configuração por Níveis
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenNivelDialog()}
                disabled={niveis.length >= 5}
                size="small"
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Adicionar Nível
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                      fontWeight: 700,
                      fontSize: '0.73rem',
                      py: 0.8,
                      borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                    },
                  }}>
                    <TableCell>Nível</TableCell>
                    <TableCell>Valor do Desconto</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {niveis.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                          <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Nenhum nível configurado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      niveis.map((nivel) => (
                        <TableRow key={nivel.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                          <TableCell>
                            <Chip 
                              label={`Nível ${nivel.nivel}`} 
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            {marketingMultinivelService.formatCurrency(nivel.valor_desconto)}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={nivel.ativo ? 'Ativo' : 'Inativo'}
                              color={nivel.ativo ? 'success' : 'default'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(nivel.createdAt).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Editar">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenNivelDialog(nivel)}
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteNivel(nivel)}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
      </Grid>

      {/* Dialog para Criar/Editar Nível */}
      <Dialog open={nivelDialog.open} onClose={handleCloseNivelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {nivelDialog.isEdit ? 'Editar Nível' : 'Criar Novo Nível'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nível"
                type="number"
                value={nivelForm.nivel}
                onChange={(e) => setNivelForm(prev => ({ 
                  ...prev, 
                  nivel: parseInt(e.target.value) || 1 
                }))}
                inputProps={{ min: 1, max: 5 }}
                disabled={nivelDialog.isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Valor do Desconto (R$)"
                type="number"
                value={nivelForm.valor_desconto}
                onChange={(e) => setNivelForm(prev => ({ 
                  ...prev, 
                  valor_desconto: parseFloat(e.target.value) || 0 
                }))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={nivelForm.ativo}
                    onChange={(e) => setNivelForm(prev => ({ ...prev, ativo: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: '0.9rem' }}>Nível Ativo</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={handleCloseNivelDialog} size="small" sx={{ fontSize: '0.8rem' }}>Cancelar</Button>
          <Button onClick={handleSaveNivel} variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>
            {nivelDialog.isEdit ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParametrosMarketing; 